
import { LetterBox } from "./LetterBox";
import { AnswerRow } from "./AnswerRow";
import React, { useState, useCallback, memo, useRef, useEffect } from "react";
import { timeoutManager } from "@/lib/managers/TimeoutManager";
import { animationCoordinator } from "@/lib/managers/AnimationCoordinator";
import { cn } from "@/lib/utils";

interface WordRowProps {
  word: string;
  guess: { letter: string; status: string }[] | null;
  currentGuess: string | null;
  isRevealed: boolean;
  isActive: boolean;
  isCompleted?: boolean;
  isInvalid?: boolean;
  isCorrectGuess?: boolean;
  isLastIncorrectGuess?: boolean;
  isUltimateGuess?: boolean;
  onAnimationComplete?: () => void;
}

export const WordRow = memo(({ 
  word, 
  guess, 
  currentGuess, 
  isRevealed, 
  isActive,
  isCompleted,
  isInvalid,
  isCorrectGuess,
  isLastIncorrectGuess,
  isUltimateGuess,
  onAnimationComplete
}: WordRowProps) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isFadedOut, setIsFadedOut] = useState(false);
  const animationIdRef = useRef<string>(`word-row-${word}-${Date.now()}`);
  const previousGuess = useRef(guess);
  const mountedRef = useRef(false);
  
  // CSS classes for the row
  const rowClassName = cn(
    "letter-grid grid relative",
    isInvalid ? 'animate-shake' : '',
    isCorrectGuess ? 'animate-flip-up' : '',
    isUltimateGuess && !isCorrectGuess && isRevealed ? 'animate-row-fade-out' : '',
    isCompleted ? 'opacity-0' : '',
    !isCompleted && isRevealed ? 'animate-fade-in' : ''
  );
  
  // Check for small screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 375);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Set mounted flag to prevent flash of content on initial render
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reset animation state when word or guess changes
  useEffect(() => {
    if (word !== previousGuess.current?.map(g => g.letter).join('')) {
      setShowAnswer(false);
      setIsFadedOut(false);
    }
    
    previousGuess.current = guess;
  }, [word, guess]);

  // Register with animation coordinator for ultimate incorrect guess
  useEffect(() => {
    // Clean up any existing animation for this row
    animationCoordinator.cancelAnimation(animationIdRef.current);
    
    // Only set up animation sequence for ultimate incorrect guess
    if (isUltimateGuess && !isCorrectGuess && isRevealed) {
      console.log('WordRow - Ultimate incorrect guess detected, setting up answer reveal');
      
      // Register new animation with coordinator
      animationCoordinator.registerAnimation(
        animationIdRef.current, 
        word.length,
        onAnimationComplete
      );
      
      // Start fade-out
      animationCoordinator.updateStage(animationIdRef.current, 'fadeOut');
      timeoutManager.setTimeout(`word-row-${word}-fade`, () => {
        if (!mountedRef.current) return;
        
        console.log('WordRow - Fading out guess');
        setIsFadedOut(true);
        
        // After fade-out, start reveal
        timeoutManager.setTimeout(`word-row-${word}-reveal`, () => {
          if (!mountedRef.current) return;
          
          console.log('WordRow - Showing answer');
          setShowAnswer(true);
          animationCoordinator.updateStage(animationIdRef.current, 'revealing');
          
          // Special case for level 1
          if (word.length === 3) {
            timeoutManager.setTimeout(`word-row-${word}-complete-l1`, () => {
              if (!mountedRef.current) return;
              animationCoordinator.updateStage(animationIdRef.current, 'completed');
            }, 800);
          }
        }, 300);
      }, 50);
    }
    
    return () => {
      // Clean up all timeouts and animations on unmount
      timeoutManager.clearTimeout(`word-row-${word}-fade`);
      timeoutManager.clearTimeout(`word-row-${word}-reveal`);
      timeoutManager.clearTimeout(`word-row-${word}-complete-l1`);
      animationCoordinator.cancelAnimation(animationIdRef.current);
    };
  }, [isUltimateGuess, isCorrectGuess, isRevealed, word, onAnimationComplete]);

  // Get letter status
  const getLetterStatus = useCallback((index: number): "correct" | "wrong-position" | "incorrect" | "empty" | "completed" => {
    if (!isRevealed) return "empty";
    if (isCompleted) return "completed";
    if (!isActive) return "empty";
    
    if (guess && guess[index]) {
      return guess[index].status === "misplaced" ? "wrong-position" : 
             guess[index].status as "correct" | "wrong-position" | "incorrect" | "empty" | "completed";
    }
    
    if (currentGuess && currentGuess[index]) {
      return "empty";
    }
    
    return "empty";
  }, [isRevealed, isCompleted, isActive, guess, currentGuess]);

  // Get letter for display
  const getLetter = useCallback((index: number): string => {
    if (guess && guess[index]) {
      return guess[index].letter;
    }
    if (currentGuess && currentGuess[index]) {
      return currentGuess[index];
    }
    return "";
  }, [guess, currentGuess]);

  // Animation state flags
  const isShaking = isInvalid;
  const shouldFadeOut = isUltimateGuess && !isCorrectGuess && isRevealed;
  const shouldHideRow = isFadedOut || (shouldFadeOut && showAnswer);
  
  // Animation event handlers
  const handleAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    console.log('WordRow - Animation ended:', e.animationName);
    
    // When the full row fade-out animation completes for failed ultimate guess
    if (e.animationName === 'row-fade-out' && 
        isUltimateGuess && !isCorrectGuess) {
      console.log('WordRow - Full row fade out complete, now showing answer');
      
      // Mark as faded out to prevent reappearance 
      setIsFadedOut(true);
      
      // Show answer immediately to prevent any intermediate empty boxes
      setShowAnswer(true);
    }
    
    // When correct guess finishes flipping
    if (e.animationName === 'flip-up' && isCorrectGuess && onAnimationComplete) {
      console.log('WordRow - Correct guess flip animation complete');
      onAnimationComplete();
    }
  }, [isUltimateGuess, isCorrectGuess, onAnimationComplete]);

  // Answer animation completion callback - now coordinated with AnimationCoordinator
  const handleAnswerAnimationComplete = useCallback(() => {
    console.log('WordRow - Answer animation complete via coordinator');
  }, []);

  // Special case for level 1: directly show answer when it's the ultimate guess and incorrect
  // Skip showing any empty boxes for level 1
  if (word.length === 3 && isUltimateGuess && !isCorrectGuess && showAnswer) {
    // Show only the answer row for level 1 ultimate incorrect guess
    return (
      <div className="relative flex justify-center" style={{ zIndex: 10 }}>
        <AnswerRow 
          word={word} 
          show={true}
          onAnimationComplete={onAnimationComplete}
        />
      </div>
    );
  }

  // For other levels, handle the fade-out transition
  return (
    <div className="relative flex justify-center" style={{ zIndex: 10 }}>
      {/* Only render the guess row if it shouldn't be hidden */}
      {!shouldHideRow && (
        <div 
          className={rowClassName}
          style={{ 
            gridTemplateColumns: `repeat(${word.length}, 1fr)`,
            gap: isSmallScreen ? '1px' : '2px',
            position: 'relative',
            zIndex: 10
          }}
          onAnimationEnd={handleAnimationEnd}
          data-guess-row="true"
        >
          {Array.from({ length: word.length }).map((_, index) => (
            <LetterBox
              key={`${word}-${index}`}
              letter={getLetter(index)}
              status={getLetterStatus(index)}
              index={index}
            />
          ))}
        </div>
      )}
      
      {/* Answer row - always render for ultimate incorrect guess but control visibility with 'show' prop */}
      {isUltimateGuess && !isCorrectGuess && (
        <AnswerRow 
          word={word} 
          show={showAnswer || isFadedOut}
          onAnimationComplete={onAnimationComplete}
        />
      )}
    </div>
  );
});

WordRow.displayName = 'WordRow';
